// jks
/**
 * btSearchMAC.js
 * This file contains an implementation of the iterative backtracking search algorithm
 * with the Maintaining Arc Consistency (MAC).
 */
let constraintMap;

/**
 * Checks if the assignment of value v1 to variable var1 breaks a constraint between var1 and var2 when var2 is assigned value v2
 * @param {*} var1 The first variable
 * @param {*} v1 The value to be assigned to var1
 * @param {*} var2 The second variable
 * @param {*} v2 The value to be assigned to var2
 * @returns {boolean} Whether the variable-value-pairs break the constraint
 */
function check(var1, v1, var2, v2) {
  let found = false;
  let var1var2Ord = true;
  let con;
  // Look for constraint between the variables
  if (
    constraintMap.get(`${var1.variableName} ${var2.variableName}`) !== undefined
  ) {
    con = constraintMap.get(`${var1.variableName} ${var2.variableName}`);
    found = true;
  } else if (
    constraintMap.get(`${var2.variableName} ${var1.variableName}`) !== undefined
  ) {
    con = constraintMap.get(`${var2.variableName} ${var1.variableName}`);
    found = true;
    var1var2Ord = false;
  }
  if (!found) {
    return true;
  }
  if (var1var2Ord) {
    let constraintBroken = false;
    let i = 0;
    while (!constraintBroken && i < con.length) {
      if (!con[i].definition(v1, v2)) {
        constraintBroken = true;
      }
      i++;
    }
    return !constraintBroken;
  }
  let constraintBroken = false;
  let i = 0;
  while (!constraintBroken && i < con.length) {
    if (!con[i].definition(v2, v1)) {
      constraintBroken = true;
    }
    i++;
  }
  return !constraintBroken;
}

/**
 * This function checks for consistency between two variables in a CSP (Constraint Satisfaction Problem).
 * It takes two variables, x and y, and checks if there is any value that is consistent between both.
 * If there is not, it removes the value from x and returns true. If there is, it returns false.
 * This helps to reduce the solution space by removing values that are not consistent with other variables in the CSP.
 *
 * @param x The first variable
 * @param y The second variable
 * @return boolean True if the revise operation was successful, false otherwise
 */
function Revise(x, y) {
  let revised = false;
  // Iterate through all values of x
  const activeXDomain = x.currentDomain.getActiveApplicants();
  for (let i = 0; i < activeXDomain.length; i++) {
    const xVal = activeXDomain[i];
    let isConsistent = false;
    // Iterate through all values of y
    const activeYDomain = y.currentDomain.getActiveApplicants();
    for (let j = 0; j < activeYDomain.length; j++) {
      const yVal = activeYDomain[j];

      // If there is a value that is consistent between x and y, set isConsistent to true
      if (check(x, xVal, y, yVal)) {
        isConsistent = true;
        break;
      }
    }
    // If there is no value that is consistent between x and y, remove the value from x and set revised to true
    if (!isConsistent) {
      x.currentDomain.removeApplicant(xVal);
      revised = true;
    }
  }
  return revised;
}

/**
 * This function checks for consistency between two variables in a CSP (Constraint Satisfaction Problem).
 * It takes two variables, x and y, and checks if there is any value that is consistent between both.
 * If there is not, it removes the value from x and returns true. If there is, it returns false.
 * This helps to reduce the solution space by removing values that are not consistent with other variables in the CSP.
 *
 * @param x The first variable
 * @param y The second variable
 * @param assignedVar the recently assigned variable
 * @return boolean True if the revise operation was successful, false otherwise
 */
function ReviseForMAC(x, y, assignedVar) {
  let revised = false;
  // Iterate through all values of x
  const activeXDomain = x.currentDomain.getActiveApplicants();
  for (let i = 0; i < activeXDomain.length; i++) {
    const xVal = activeXDomain[i];
    let isConsistent = false;
    // Iterate through all values of y
    const activeYDomain = y.currentDomain.getActiveApplicants();
    for (let j = 0; j < activeYDomain.length; j++) {
      const yVal = activeYDomain[j];

      // If there is a value that is consistent between x and y, set isConsistent to true
      if (check(x, xVal, y, yVal)) {
        isConsistent = true;
        break;
      }
    }
    // If there is no value that is consistent between x and y, remove the value from x and set revised to true
    if (!isConsistent) {
      x.currentDomain.removeApplicant(xVal);
      // add value to the reductions map
      if (assignedVar.reductions.has(x.variableName)) {
        assignedVar.reductions.set(x.variableName, [
          ...assignedVar.reductions.get(x.variableName),
          xVal,
        ]);
      } else {
        assignedVar.reductions.set(x.variableName, [xVal]);
      }
      revised = true;
    }
  }
  return revised;
}

/**
 * This function checks for arc consistency in a CSP (Constraint Satisfaction Problem).
 * It takes a CSP as an input, and initializes a queue with all arcs in the CSP.
 *
 * @param CSP The CSP to check
 * @return boolean True if the arc consistency operation was successful, false otherwise
 */
// eslint-disable-next-line no-unused-vars
function AC31(CSP) {
  let success = true;
  // initialize queues
  const queues = new Map();
  CSP.variables.forEach((variable) => {
    queues.set(variable.variableName, []);
    variable.neighbors.forEach((neighbor) => {
      queues.set(variable.variableName, [
        ...queues.get(variable.variableName),
        [variable, neighbor],
      ]);
    });
  });

  let queuesEmpty = false;
  while (!queuesEmpty) {
    // find the first queue that is not empty
    const keys = Array.from(queues.keys());
    let q;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (queues.get(key).length > 0) {
        q = queues.get(key);
        break;
      }
    }

    let x;
    let y;
    // eslint-disable-next-line prefer-const
    [x, y] = q.pop();
    // check if some domain values were removed
    if (Revise(x, y)) {
      if (x.currentDomain.getActiveApplicants().length === 0) {
        // false means this is a failure
        success = false;
      }
      // For each variable z in the neighbors of x, except y, enqueue the arc (z, x) into the queue of the variable z.
      // This ensures that we maintain arc consistency for all neighboring variables.
      x.neighbors.forEach((neighbor) => {
        if (neighbor.variableName !== y.variableName) {
          queues.set(neighbor.variableName, [
            ...queues.get(neighbor.variableName),
            [neighbor, x],
          ]);
        }
      });
    }
    let empty = true;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (queues.get(key).length > 0) {
        empty = false;
        break;
      }
    }
    queuesEmpty = empty;
  }
  // no failure
  return success;
}

/**
 * This function checks for arc consistency in a CSP (Constraint Satisfaction Problem).
 * It takes a CSP as an input, and initializes a queue with all arcs in the CSP.
 *
 * @param CSP The CSP to check
 * @return boolean True if the arc consistency operation was successful, false otherwise
 */
function AC31forMAC(assignedVar, variables) {
  let success = true;
  // initialize queues
  const queues = new Map();
  variables.forEach((variable) => {
    queues.set(variable.variableName, []);
    variable.neighbors.forEach((neighbor) => {
      queues.set(variable.variableName, [
        ...queues.get(variable.variableName),
        [variable, neighbor],
      ]);
    });
  });

  let queuesEmpty = false;
  while (!queuesEmpty) {
    // find the first queue that is not empty
    const keys = Array.from(queues.keys());
    let q;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (queues.get(key).length > 0) {
        q = queues.get(key);
        break;
      }
    }

    let x;
    let y;
    // eslint-disable-next-line prefer-const
    [x, y] = q.pop();
    // check if some domain values were removed
    if (ReviseForMAC(x, y, assignedVar)) {
      if (x.currentDomain.getActiveApplicants().length === 0) {
        // false means this is a failure
        success = false;
      }
      // For each variable z in the neighbors of x, except y, enqueue the arc (z, x) into the queue of the variable z.
      // This ensures that we maintain arc consistency for all neighboring variables.
      x.neighbors.forEach((neighbor) => {
        if (neighbor.variableName !== y.variableName && neighbor.variableName !== assignedVar.variableName) {
          if (queues.has(neighbor.variableName)) {
            queues.set(neighbor.variableName, [
              ...queues.get(neighbor.variableName),
              [neighbor, x],
            ]);
          } else {
            queues.set(neighbor.variableName, [[neighbor, x]]);
          }
        }
      });
    }
    let empty = true;
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if (queues.get(key).length > 0) {
        empty = false;
        break;
      }
    }
    queuesEmpty = empty;
  }
  // no failure
  return success;
}

/**
 * Updates the domain of the variable by removing values that are inconsistent with the assignment.
 * @param {*} variable - The variable whose domain should be updated.
 * @param {*} assignedVar - The assigned variable.
 * @param {*} assignedValue - The value assigned to the assignedVar.
 * @returns {Array|null} - The variable's current active domain, possibly updated
 */
function updateDomain(variable, assignedVar, assignedValue) {
  const originalDomain = variable.currentDomain.getActiveApplicants();
  const { variableName } = variable;

  originalDomain.forEach((value) => {
    if (!check(assignedVar, assignedValue, variable, value)) {
      variable.currentDomain.removeApplicant(value);
      // add value to the reductions map
      if (assignedVar.reductions.has(variableName)) {
        assignedVar.reductions.set(variableName, [
          ...assignedVar.reductions.get(variableName),
          value,
        ]);
      } else {
        assignedVar.reductions.set(variableName, [value]);
      }
    }
  });

  return variable.currentDomain.getActiveApplicants();
}

export function btSearchMAC(cspInstance) {
  constraintMap = cspInstance.constraintMap;
  AC31(cspInstance);

  let bestLen = 0;
  let bestSol;
  const { variables, variableMap } = cspInstance;

  // Initialize the stack with the first variable and its domain.
  const stack = [
    {
      /** index of variable in the partial solution */
      variableIndex: 0,
      /** index of variable in the variables array */
      varIndex: 0,
    },
  ];
  while (stack.length > 0) {
    const frame = stack.pop();

    if (frame.variableIndex === variables.length) {
      // All variables have been assigned, a solution has been found.
      return variables.map((variable) => ({
        variable: variable.variableName,
        assignment: variable.currentDomain.getAssignment(),
      }));
    }
    const variable = variables[frame.varIndex];
    const activeApplicants = variable.currentDomain.getActiveApplicants();

    if (activeApplicants.length > 0) {
      // Assign a value to the current variable
      const value = activeApplicants[0];
      variable.currentDomain.assign(value);

      // -------------------
      const newSol = variables
        .map((varb) => ({
          variable: varb.variableName,
          assignment: varb.currentDomain.getAssignment(),
        }))
        .filter((assignment) => assignment.assignment !== undefined)
        .slice();
      const newLen = newSol.length;
      if (newLen > bestLen) {
        bestLen = newLen;
        bestSol = newSol;
      }
      if (bestLen === variables.length) {
        return variables.map((varb) => ({
          variable: varb.variableName,
          assignment: varb.currentDomain.getAssignment(),
        }));
      }
      // -------------------

      // Apply MAC filtering to the future variables and check if all their domains are wiped out.
      let allWipedOut = true;

      // update domains for all future variables

      for (let i = frame.varIndex + 1; i < variables.length; i++) {
        updateDomain(variables[i], variable, value);
      }

      const currVarRemovedVals = [];
      variable.currentDomain.getActiveApplicants().forEach((applicant) => {
        if (applicant.vName !== value.vName) {
          currVarRemovedVals.push(applicant);
        }
      });
      currVarRemovedVals.forEach((val) => {
        variable.currentDomain.removeApplicant(val);
      });

      AC31forMAC(variable, variable.neighbors);

      for (let i = frame.varIndex + 1; i < variables.length; i++) {
        if (variables[i].currentDomain.getActiveApplicants().length > 0) {
          allWipedOut = false;
        }
      }

      // add applicants back in after AC
      currVarRemovedVals.forEach((val) => {
        variable.currentDomain.addApplicant(val);
      });

      if (!allWipedOut) {
        // Not all future variables are wiped out, push the current variable and its domain to the stack and move to the next variable.
        stack.push(frame);

        let nextVariable;
        // find next variable that is not wiped out
        for (let i = frame.varIndex + 1; i < variables.length; i++) {
        // variable with active domain values
          if (variables[i].currentDomain.getActiveApplicants().length > 0) {
            nextVariable = [variables[i], i];
            break;
          }
        }

        // push next variable onto the stack
        if (nextVariable !== undefined) {
          stack.push({
            variableIndex: frame.variableIndex + 1,
            varIndex: nextVariable[1],
          });
        }
      } else {
        // all future variables are wiped out due to the given assignment

        // remove assignment and remove value from domain
        variable.currentDomain.unassign();
        // push value to removedValues
        variable.removedValues.push(value);

        // Restore the wiped out future variables' domains.
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of variable.reductions) {
          variableMap.get(key).currentDomain.addApplicants(val);
        }
        // clear the variable's reductions map
        variable.reductions.clear();

        // Continue with the next value of the current variable.
        stack.push(frame);
      }
    } else {
      // no values to assign to current variable meaning everything is wiped out (backtracking condition)

      // if there are previous variables
      if (stack.length > 0) {
        // add domain values back that were removed (retaining the filtering done by previous variables)
        variable.currentDomain.addApplicants(variable.removedValues);
        variable.removedValues = [];

        const prevFrame = stack.pop();
        const prevVar = variables[prevFrame.varIndex];
        // push assigned value to removed values
        prevVar.removedValues.push(prevVar.currentDomain.getAssignment());
        // remove assignment from previous variable and its domain
        prevVar.currentDomain.unassign();

        // Restore the wiped out future variables' domains done by the previous variable.
        // eslint-disable-next-line no-restricted-syntax
        for (const [key, val] of prevVar.reductions) {
          variableMap.get(key).currentDomain.addApplicants(val);
        }
        // clear the previous variable's reductions map
        prevVar.reductions.clear();

        // try next value of previous variable
        stack.push(prevFrame);
      }
    }
  }

  return bestSol;
}
